import { useState } from 'react'
import {
  ArrowDown,
  ArrowUp,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  useCompanyDispositions,
  useCreateDisposition,
  useDeleteDisposition,
  useUpdateDisposition,
} from '@/api/powerDialer'
import type { Disposition, DispositionCategory } from '@/types/powerDialer'
import {
  CATEGORY_OPTIONS,
  styleForCategory,
} from '@/lib/dispositionCategory'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CompanyDispositionsDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation()
  const query = useCompanyDispositions()
  const createMut = useCreateDisposition()
  const updateMut = useUpdateDisposition()
  const deleteMut = useDeleteDisposition()

  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [newCategory, setNewCategory] = useState<DispositionCategory>('neutral')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')

  const dispositions = query.data ?? []

  const handlePatch = async (d: Disposition, patch: Partial<Disposition>) => {
    try {
      await updateMut.mutateAsync({ id: d.id, patch })
    } catch {
      // toast géré ailleurs
    }
  }

  const handleAdd = async () => {
    if (!newName.trim()) return
    const slug = newName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
    try {
      await createMut.mutateAsync({
        slug,
        name: newName.trim(),
        category: newCategory,
        campaign: null,
        order: (dispositions[dispositions.length - 1]?.order ?? 0) + 10,
      })
      setNewName('')
      setNewCategory('neutral')
      setAdding(false)
    } catch {
      // toast géré ailleurs
    }
  }

  const handleDelete = async (d: Disposition) => {
    try {
      await deleteMut.mutateAsync(d.id)
    } catch {
      // toast géré ailleurs
    }
  }

  const handleMove = async (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= dispositions.length) return
    const current = dispositions[index]
    const other = dispositions[targetIndex]
    try {
      await Promise.all([
        updateMut.mutateAsync({ id: current.id, patch: { order: other.order } }),
        updateMut.mutateAsync({ id: other.id, patch: { order: current.order } }),
      ])
    } catch {
      // toast géré ailleurs
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t('manager.powerDialer.dispositions.companyDialogTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('manager.powerDialer.dispositions.companyDialogDescription')}
          </DialogDescription>
        </DialogHeader>

        {query.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
            {dispositions.map((d, index) => {
              const catStyle = styleForCategory(d.category)
              const Icon = catStyle.icon
              const isEditing = editingId === d.id
              return (
                <div
                  key={d.id}
                  className="flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 px-2 py-1.5"
                >
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => void handleMove(index, -1)}
                      className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed leading-none"
                    >
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      disabled={index === dispositions.length - 1}
                      onClick={() => void handleMove(index, 1)}
                      className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed leading-none"
                    >
                      <ArrowDown className="w-3 h-3" />
                    </button>
                  </div>
                  <span
                    className={cn(
                      'w-5 h-5 rounded flex items-center justify-center shrink-0 border',
                      catStyle.badgeLight,
                      'dark:hidden',
                    )}
                  >
                    <Icon className="w-3 h-3" />
                  </span>
                  <span
                    className={cn(
                      'w-5 h-5 rounded items-center justify-center shrink-0 border hidden dark:flex',
                      catStyle.badgeDark,
                    )}
                  >
                    <Icon className="w-3 h-3" />
                  </span>
                  {isEditing ? (
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onBlur={() => {
                        if (editName.trim() && editName !== d.name) {
                          void handlePatch(d, { name: editName.trim() })
                        }
                        setEditingId(null)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur()
                        if (e.key === 'Escape') setEditingId(null)
                      }}
                      autoFocus
                      className="h-7 text-xs flex-1"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingId(d.id)
                        setEditName(d.name)
                      }}
                      className="flex-1 text-left text-sm text-slate-900 dark:text-white truncate"
                    >
                      {d.name}
                    </button>
                  )}

                  <select
                    value={d.category}
                    onChange={(e) =>
                      void handlePatch(d, {
                        category: e.target.value as DispositionCategory,
                      })
                    }
                    title={t('manager.powerDialer.dispositions.categoryTitle')}
                    className="text-[11px] bg-transparent border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5 max-w-[11rem]"
                  >
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {t(`manager.powerDialer.dispositions.categoryOptions.${c.value}`, c.label)}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => void handleDelete(d)}
                    className="p-1 text-slate-400 hover:text-red-500"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )
            })}
          </div>
        )}

        <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
          {adding ? (
            <div className="flex items-center gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t('manager.powerDialer.dispositions.namePlaceholder')}
                className="h-8 text-xs flex-1"
                autoFocus
              />
              <select
                value={newCategory}
                onChange={(e) =>
                  setNewCategory(e.target.value as DispositionCategory)
                }
                className="text-xs bg-transparent border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5"
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {t(`manager.powerDialer.dispositions.categoryOptions.${c.value}`, c.label)}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                size="sm"
                onClick={() => void handleAdd()}
                disabled={!newName.trim() || createMut.isPending}
              >
                {createMut.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  t('manager.powerDialer.dispositions.add')
                )}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setAdding(false)
                  setNewName('')
                }}
              >
                {t('manager.powerDialer.dispositions.cancel')}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAdding(true)}
            >
              <Plus className="w-3 h-3 mr-1" />
              {t('manager.powerDialer.dispositions.addDisposition')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
